import {
  type FormEvent,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Bell,
  FileText,
  Mail,
  Paperclip,
  Send,
  Settings,
  ShieldCheck,
  Sparkles,
  Upload,
  Users,
  X,
} from 'lucide-react';
import { api, formatBytes, uploadBinary } from '../../api';
import { useAuth } from '../../auth';
import type {
  Company,
  CompanyContact,
  MailMessage,
  Queue,
  Template,
  Ticket,
} from '../../app/legacy-types';
import { defaultWorkCategory, type WorkCategory } from '../worklogs/worklog-model';

const PRIORITY_OPTIONS = [
  { value: 'LOW', label: 'Низкий' },
  { value: 'NORMAL', label: 'Обычный' },
  { value: 'HIGH', label: 'Высокий' },
  { value: 'CRITICAL', label: 'Критический' },
];

export type NewTicketInitialDraft = {
  companyId?: string;
  contactId?: string;
  queueId?: string;
  subject?: string;
  description?: string;
  requesterFullName?: string;
  requesterPhone?: string;
  requesterEmail?: string;
  priority?: string;
  category?: string;
  workCategoryId?: string;
  mailMessageId?: string;
  telephonyCallId?: string;
  telephonyPhone?: string;
  telephonyContact?: string;
  contactConversationId?: string;
  contextDescription?: string;
};

type NewTicketProps = {
  onClose: () => void;
  initialDraft?: NewTicketInitialDraft;
  onCreated?: (ticket: Ticket) => void | Promise<void>;
};

function formatPhoneInput(value: string) {
  let digits = value.replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('8')) digits = `7${digits.slice(1)}`;
  if (!digits.startsWith('7')) digits = `7${digits}`;
  digits = digits.slice(0, 11);
  const local = digits.slice(1);
  let result = '+7';
  if (local.length) result += ` (${local.slice(0, 3)}`;
  if (local.length >= 3) result += ')';
  if (local.length > 3) result += ` ${local.slice(3, 6)}`;
  if (local.length > 6) result += `-${local.slice(6, 8)}`;
  if (local.length > 8) result += `-${local.slice(8, 10)}`;
  return result;
}

function handlePhoneDelete(event: KeyboardEvent<HTMLInputElement>, setValue: (value: string) => void) {
  if (event.key !== 'Backspace' && event.key !== 'Delete') return;
  const input = event.currentTarget;
  const start = input.selectionStart ?? 0;
  const end = input.selectionEnd ?? start;
  if (start !== end) return;
  const value = input.value;
  const direction = event.key === 'Backspace' ? -1 : 1;
  let index = event.key === 'Backspace' ? start - 1 : start;
  if (index < 0 || index >= value.length || /\d/.test(value[index])) return;
  while (index >= 0 && index < value.length && !/\d/.test(value[index])) index += direction;
  if (index < 0 || index >= value.length) {
    index = event.key === 'Backspace' ? start : start - 1;
    while (index >= 0 && index < value.length && !/\d/.test(value[index])) index -= direction;
  }
  if (index < 0 || index >= value.length) return;
  event.preventDefault();
  const formatted = formatPhoneInput(`${value.slice(0, index)}${value.slice(index + 1)}`);
  setValue(formatted);
  requestAnimationFrame(() => {
    const cursor = Math.min(start, formatted.length);
    input.setSelectionRange(cursor, cursor);
  });
}

function contactValue(contact: CompanyContact, type: 'PHONE' | 'EMAIL') {
  const channels = contact.channels.filter(channel => channel.is_active !== false && channel.channel_type === type && channel.value.trim());
  const preferred = channels.find(channel => channel.is_primary) ?? channels[0];
  return preferred?.value || (type === 'PHONE' ? contact.phone : contact.email) || '';
}

function formatDateTime(value?: string | null) {
  if (!value) return 'Дата не указана';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('ru-RU', { dateStyle: 'short', timeStyle: 'short' }).format(date);
}

async function uploadFile(ticketId: string, file: File) {
  const start = await api<{ id: string; partSize: number; partsCount: number }>('/files/multipart/start', {
    method: 'POST',
    body: JSON.stringify({ ticketId, fileName: file.name, mimeType: file.type || 'application/octet-stream', size: file.size }),
  });
  const parts: Array<{ PartNumber: number; ETag?: string }> = [];
  for (let partNumber = 1; partNumber <= start.partsCount; partNumber += 1) {
    const from = (partNumber - 1) * start.partSize;
    const to = Math.min(file.size, from + start.partSize);
    const uploaded = await uploadBinary<{ PartNumber: number; ETag?: string }>(`/files/multipart/${start.id}/parts/${partNumber}`, file.slice(from, to));
    parts.push({ PartNumber: uploaded.PartNumber || partNumber, ETag: uploaded.ETag });
  }
  await api(`/files/multipart/${start.id}/complete`, { method: 'POST', body: JSON.stringify({ parts }) });
}

export function NewTicket({ onClose, initialDraft = {}, onCreated }: NewTicketProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const telephonyCallId = initialDraft.telephonyCallId ?? searchParams.get('telephonyCall') ?? '';
  const telephonyPhone = initialDraft.telephonyPhone ?? searchParams.get('phone') ?? '';
  const telephonyContact = initialDraft.telephonyContact ?? searchParams.get('contact') ?? '';
  const telephonyCompanyId = initialDraft.companyId ?? searchParams.get('companyId') ?? '';
  const directoryContactId = initialDraft.contactId ?? searchParams.get('contactId') ?? '';
  const mailMessageId = initialDraft.mailMessageId ?? searchParams.get('mailMessage') ?? '';
  const [templateId, setTemplateId] = useState('');
  const [subject, setSubject] = useState(initialDraft.subject ?? searchParams.get('subject') ?? (telephonyCallId ? `Телефонный звонок от ${telephonyContact || telephonyPhone}` : ''));
  const [description, setDescription] = useState(initialDraft.description ?? (telephonyCallId ? `Обращение принято по телефону. Номер: ${telephonyPhone || 'не определён'}.` : ''));
  const [subjectHint, setSubjectHint] = useState('Кратко опишите проблему или запрос');
  const [descriptionHint, setDescriptionHint] = useState('Что произошло, когда началось, какие действия уже предпринимались?');
  const [priority, setPriority] = useState(initialDraft.priority ?? 'NORMAL');
  const [category, setCategory] = useState(initialDraft.category ?? (telephonyCallId ? 'Телефония' : ''));
  const [workCategoryId, setWorkCategoryId] = useState(initialDraft.workCategoryId ?? '');
  const [queueId, setQueueId] = useState(initialDraft.queueId ?? '');
  const [requesterPhone, setRequesterPhone] = useState(formatPhoneInput(initialDraft.requesterPhone ?? telephonyPhone));
  const [requesterFullName, setRequesterFullName] = useState(initialDraft.requesterFullName ?? (telephonyContact || user?.fullName || ''));
  const [requesterEmail, setRequesterEmail] = useState(initialDraft.requesterEmail ?? user?.email ?? '');
  const [companyId, setCompanyId] = useState(telephonyCompanyId);
  const [contactId, setContactId] = useState(directoryContactId);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [draggingFiles, setDraggingFiles] = useState(false);
  const [error, setError] = useState('');
  const { data: companies = [] } = useQuery({ queryKey: ['companies'], queryFn: () => api<Company[]>('/companies'), enabled: user?.userType === 'EMPLOYEE' });
  const { data: companyContacts = [] } = useQuery({ queryKey: ['contacts', 'ticket-create', companyId], queryFn: () => api<CompanyContact[]>(`/contacts?companyId=${companyId}&active=true`), enabled: user?.userType === 'EMPLOYEE' && Boolean(companyId) });
  const { data: queues = [] } = useQuery({ queryKey: ['queues'], queryFn: () => api<Queue[]>('/queues'), enabled: user?.userType === 'EMPLOYEE' });
  const { data: templates = [] } = useQuery({ queryKey: ['templates'], queryFn: () => api<Template[]>('/templates'), enabled: user?.userType === 'EMPLOYEE' });
  const { data: workCategories = [] } = useQuery({ queryKey: ['work-categories'], queryFn: () => api<WorkCategory[]>('/work-categories'), enabled: user?.userType === 'EMPLOYEE' });
  const { data: sourceMail } = useQuery({ queryKey: ['mail-message-source', mailMessageId], queryFn: () => api<MailMessage>(`/mail/messages/${mailMessageId}`), enabled: user?.userType === 'EMPLOYEE' && Boolean(mailMessageId) });
  const create = useMutation({
    mutationFn: async ({ payload, files }: { payload: Record<string, unknown>; files: File[] }) => {
      const ticket = await api<Ticket>('/tickets', { method: 'POST', body: JSON.stringify(payload) });
      const failures = [...(ticket.attachment_transfer?.failures ?? [])];
      for (const file of files) {
        try { await uploadFile(ticket.id, file); }
        catch { failures.push(file.name); }
      }
      return { ticket, failures };
    },
    onSuccess: async ({ ticket, failures }) => {
      if (telephonyCallId) await api(`/telephony/calls/${telephonyCallId}/ticket`, { method: 'PATCH', body: JSON.stringify({ ticketId: ticket.id }) }).catch(() => undefined);
      if (failures.length) window.alert(`Заявка создана, но часть файлов не удалось перенести: ${failures.join(', ')}`);
      await onCreated?.(ticket);
      navigate(`/tickets/${ticket.id}`);
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Ошибка создания'),
  });

  useEffect(() => {
    if (!workCategories.length) return;
    setWorkCategoryId(current => workCategories.some(item => item.is_active && item.id === current) ? current : defaultWorkCategory(workCategories));
  }, [workCategories]);

  function applyTemplate(id: string) {
    setTemplateId(id);
    const template = templates.find(item => item.id === id);
    if (!template) {
      setSubjectHint('Кратко опишите проблему или запрос');
      setDescriptionHint('Что произошло, когда началось, какие действия уже предпринимались?');
      return;
    }
    setSubject('');
    setDescription('');
    setSubjectHint(template.subject_template);
    setDescriptionHint(template.body_template);
    setPriority(template.priority || 'NORMAL');
    setCategory(template.category || '');
    setQueueId(template.queue_id || '');
  }

  useEffect(() => {
    const requestedTemplate = searchParams.get('templateId');
    if (requestedTemplate && templates.length && requestedTemplate !== templateId) applyTemplate(requestedTemplate);
  }, [templates, searchParams]);

  useEffect(() => {
    if (!sourceMail) return;
    setSubject(sourceMail.subject || 'Обращение по электронной почте');
    setDescription(sourceMail.text_body || sourceMail.snippet || sourceMail.subject);
    setRequesterFullName(sourceMail.from_name || sourceMail.from_address || 'Отправитель письма');
    setRequesterEmail(sourceMail.from_address || '');
    if (sourceMail.company_id) setCompanyId(sourceMail.company_id);
    setCategory('Почта');
  }, [sourceMail]);

  useEffect(() => {
    if (!contactId) return;
    const contact = companyContacts.find(item => item.id === contactId);
    if (!contact) return;
    setRequesterFullName(contact.full_name || '');
    setRequesterPhone(formatPhoneInput(contactValue(contact, 'PHONE')));
    setRequesterEmail(contactValue(contact, 'EMAIL'));
  }, [contactId, companyContacts]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, []);

  const hasDraft = Boolean(subject.trim() || description.trim() || templateId || companyId || contactId || queueId || category.trim() || requesterPhone.trim() || pendingFiles.length);

  function requestClose() {
    if (create.isPending) return;
    if (hasDraft && !window.confirm('Закрыть форму? Несохранённые данные заявки будут потеряны.')) return;
    onClose();
  }

  useEffect(() => {
    const handleEscape = (event: globalThis.KeyboardEvent) => { if (event.key === 'Escape') requestClose(); };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [hasDraft, create.isPending]);

  function appendFiles(files: FileList | File[]) {
    const incoming = Array.from(files);
    setPendingFiles(current => {
      const keys = new Set(current.map(file => `${file.name}:${file.size}:${file.lastModified}`));
      return [...current, ...incoming.filter(file => !keys.has(`${file.name}:${file.size}:${file.lastModified}`))].slice(0, 20);
    });
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    const phoneDigits = requesterPhone.replace(/\D/g, '');
    if (requesterPhone && phoneDigits.length !== 11) {
      setError('Введите корректный номер телефона в формате +7 (999) 999-99-99');
      return;
    }
    const finalSubject = subject.trim() || subjectHint.trim();
    const finalDescription = description.trim() || descriptionHint.trim();
    if (!finalSubject || !finalDescription) {
      setError('Заполните тему и описание заявки');
      return;
    }
    create.mutate({
      payload: {
        companyId: user?.userType === 'EMPLOYEE' ? companyId : undefined,
        contactId: user?.userType === 'EMPLOYEE' ? contactId || undefined : undefined,
        queueId: user?.userType === 'EMPLOYEE' ? queueId || undefined : undefined,
        subject: finalSubject,
        description: finalDescription,
        requesterFullName,
        requesterPhone,
        requesterEmail,
        priority,
        category,
        workCategoryId: user?.userType === 'EMPLOYEE' ? workCategoryId || undefined : undefined,
        mailMessageId: mailMessageId || undefined,
        contactConversationId: initialDraft.contactConversationId || undefined,
      },
      files: pendingFiles,
    });
  }

  const modalDescription = initialDraft.contextDescription || (mailMessageId
    ? 'Заявка будет связана с выбранным письмом'
    : telephonyCallId
      ? 'Заявка будет связана с телефонным звонком'
      : 'Заполните обращение, не покидая текущий список заявок');

  return <div className="modal-backdrop ticket-create-backdrop" onMouseDown={event => { if (event.currentTarget === event.target) requestClose(); }}>
    <form className="modal-card ticket-create-modal" onSubmit={submit} onMouseDown={event => event.stopPropagation()}>
      <div className="modal-head ticket-create-modal-head">
        <div><span className="modal-kicker">НОВАЯ ЗАЯВКА</span><h2>Создание обращения</h2><p>{modalDescription}</p></div>
        <button type="button" className="icon-button" onClick={requestClose} aria-label="Закрыть форму создания заявки"><X /></button>
      </div>
      <div className="ticket-create-modal-layout">
        <div className="ticket-create-main-column">
          <section className="ticket-create-section ticket-create-main-section">
            <div className="planned-section-title"><span><FileText /></span><div><h3>Основная информация</h3><p>Тема, описание и маршрут будущей заявки</p></div></div>
            {user?.userType === 'EMPLOYEE' && <div className="template-strip ticket-create-template-strip"><label><Sparkles /> Шаблон<select value={templateId} onChange={event => applyTemplate(event.target.value)}><option value="">Без шаблона</option>{templates.filter(item => item.is_active).map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label><small>Шаблон заполнит тему, описание и рекомендуемые параметры</small></div>}
            {user?.userType === 'EMPLOYEE' && <div className="form-grid three"><label>Компания *<select name="companyId" required value={companyId} onChange={event => { setCompanyId(event.target.value); setContactId(''); }}><option value="">Выберите компанию</option>{companies.filter(item => item.is_active).map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label><label>Контакт<select name="contactId" value={contactId} onChange={event => setContactId(event.target.value)} disabled={!companyId}><option value="">Без привязки</option>{companyContacts.filter(item => item.is_active).map(item => <option key={item.id} value={item.id}>{item.full_name || item.email || item.phone || 'Без имени'}</option>)}</select></label><label>Очередь<select name="queueId" value={queueId} onChange={event => setQueueId(event.target.value)}><option value="">По умолчанию</option>{queues.filter(item => item.is_active).map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label></div>}
            <label>Тема *<input name="subject" value={subject} onChange={event => setSubject(event.target.value)} maxLength={250} placeholder={subjectHint} /></label>
            <label>Описание *<textarea name="description" value={description} onChange={event => setDescription(event.target.value)} rows={8} placeholder={descriptionHint} /></label>
          </section>
          <section className="ticket-create-section">
            <div className="planned-section-title"><span><Users /></span><div><h3>Контакт и уведомления</h3><p>Кому сообщать об изменениях и у кого уточнять детали</p></div></div>
            <div className="form-grid three"><label>Клиент<input name="requesterFullName" value={requesterFullName} onChange={event => setRequesterFullName(event.target.value)} placeholder="Имя клиента или контактное лицо" /></label><label>Телефон<input name="requesterPhone" inputMode="tel" autoComplete="tel" value={requesterPhone} onChange={event => setRequesterPhone(formatPhoneInput(event.target.value))} onKeyDown={event => handlePhoneDelete(event, setRequesterPhone)} placeholder="+7 (___) ___-__-__" maxLength={18} /></label><label>Email для уведомлений<input name="requesterEmail" type="email" value={requesterEmail} onChange={event => setRequesterEmail(event.target.value)} /></label></div>
          </section>
          <section className="ticket-create-section ticket-create-files-section">
            <div className="planned-section-title"><span><Paperclip /></span><div><h3>Вложения</h3><p>Скриншоты, документы, логи и другие материалы</p></div><em>{pendingFiles.length ? `${pendingFiles.length} файлов` : 'Необязательно'}</em></div>
            {sourceMail && <div className="source-mail-banner"><Mail /><span><b>Исходное письмо: {sourceMail.subject}</b><small>{sourceMail.from_name || sourceMail.from_address} · {formatDateTime(sourceMail.received_at)}</small></span>{sourceMail.linked_ticket_id && <em>Уже связано с {sourceMail.ticket_display_number}</em>}</div>}
            <div className={`upload-zone ${draggingFiles ? 'dragging' : ''}`} onDragOver={event => { event.preventDefault(); setDraggingFiles(true); }} onDragLeave={() => setDraggingFiles(false)} onDrop={event => { event.preventDefault(); setDraggingFiles(false); appendFiles(event.dataTransfer.files); }}><Upload /><div><b>Перетащите файлы сюда</b><span>или выберите на компьютере, до 5 ГБ каждый</span></div><button type="button" className="soft-button" onClick={() => fileInputRef.current?.click()}>Выбрать файлы</button><input ref={fileInputRef} type="file" multiple hidden onChange={event => { if (event.target.files) appendFiles(event.target.files); event.currentTarget.value = ''; }} /></div>
            {pendingFiles.length > 0 && <div className="pending-files">{pendingFiles.map((file, index) => <span key={`${file.name}-${file.lastModified}`}><Paperclip /><b title={file.name}>{file.name}</b><small>{formatBytes(file.size)}</small><button type="button" onClick={() => setPendingFiles(current => current.filter((_item, currentIndex) => currentIndex !== index))} aria-label={`Убрать ${file.name}`}><X /></button></span>)}</div>}
          </section>
        </div>
        <aside className="ticket-create-side-column">
          <section className="ticket-create-section ticket-create-settings-section">
            <div className="planned-section-title"><span><Settings /></span><div><h3>Параметры заявки</h3><p>Приоритет, категория, SLA и уведомления</p></div></div>
            <label>Приоритет<select name="priority" value={priority} onChange={event => setPriority(event.target.value)}>{PRIORITY_OPTIONS.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
            <label>Категория заявки<input name="category" value={category} onChange={event => setCategory(event.target.value)} placeholder="Например: 1С, почта, доступы" /></label>
            {user?.userType === 'EMPLOYEE' && <label>Категория работ<select name="workCategoryId" value={workCategoryId} onChange={event => setWorkCategoryId(event.target.value)} required><option value="">Выберите категорию работ</option>{workCategories.filter(item => item.is_active).map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>}
            <div className="info-note ticket-create-info-note"><ShieldCheck /><span><b>SLA включён автоматически</b><small>Сроки реакции и решения рассчитываются по приоритету</small></span></div>
            <div className="info-note ticket-create-info-note"><Bell /><span><b>Уведомления</b><small>Изменения придут по доступным каналам</small></span></div>
          </section>
        </aside>
      </div>
      {error && <div className="form-error ticket-create-error">{error}</div>}
      <div className="form-actions ticket-create-actions"><span /><button type="button" className="button ghost" onClick={requestClose}>Отмена</button><button className="button primary" disabled={create.isPending}><Send />{create.isPending ? 'Создаём и загружаем…' : 'Создать заявку'}</button></div>
    </form>
  </div>;
}
